import React, { useState } from 'react';
import { Link, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import urljoin from 'url-join';

import Paper from '~/components/core/Atomic/Paper';
import { Tab, Tabs } from '~/components/core/Atomic/Tabs';
import { isActionAllowed } from '~/components/core/Permissions/PermissionUtils';

import { onTabClick } from '../../../components/ReportsDashboardsPage/ReportsDashboardUtils';
import { MIXPANEL_EVENTS } from '../../../pocs/mixpanel';
import { getCurrentTabValue, getTabName } from '../../../Utils';
import CardDialog from '../../CardDialog';
import mixpanel from '../../CmsMain/mixpanel';
import { useCms } from '../../hooks/useCms';
import { TableauExportActions } from '../TableauExportActions/TableauExportActions';

import { useStyles } from '../../../assets/styles';

export const HeaderTabs = ({ tabs }) => {
  const match = useRouteMatch();
  const { classes } = useStyles();
  const [{ exportTableauView }, setExportTableauView] = useState({});
  const isDashboardView = match.url.includes('dashboards') || match.url.includes('resilience_package');
  const { user, userProfile, userOrganization } = useCms();
  const isSupervisor = user?.is_org_level_supervisor ?? false;
  const allowedTabs = React.useMemo(
    () =>
      tabs.filter((tab) => {
        return isActionAllowed({
          action: tab.props?.action,
          verb: tab.props?.verb,
          profile: userProfile,
          userOrganization,
          isSupervisor,
        });
      }),
    [tabs, userProfile, userOrganization, isSupervisor]
  );

  const onFilterApplied = (filterName, filteredValues) => {
    const { parentTab, tabName } = getTabName();

    mixpanel.track(MIXPANEL_EVENTS.REPORTING_ENTITY_FILTER_APPLIED, {
      reporting_entity: parentTab,
      tab_name: tabName,
      filter_name: filterName,
      filter_value: JSON.stringify(filteredValues),
    });
  };

  return (
    <>
      <CardDialog noCardTitle height="100%">
        <Paper className={classes.reportsDashboardHeaderTab}>
          <Tabs
            value={getCurrentTabValue({ tabs: allowedTabs, match })}
            variant="standard"
            indicatorColor="primary"
            textColor="primary"
            style={{ minHeight: 40 }}
          >
            {allowedTabs.map((tab) => (
              <Tab
                key={tab.url}
                // v5 deleted the Tab's inner `wrapper` span, so the `classes={{ wrapper: 'px-16
                // text-base' }}` this used to carry has nowhere to land. `px-16` folds into the
                // inline padding below (16px horizontal, plus the 10px vertical the wrapper's own
                // rule contributed); `text-base` moves to the root, where the font-size still
                // reaches the label by inheritance.
                className="text-base"
                style={{ minHeight: 40, margin: 0, minWidth: 'auto', padding: '10px 16px' }}
                label={tab.label}
                component={Link}
                to={`${match.url}/${tab.url}`}
                onClick={onTabClick}
              />
            ))}
          </Tabs>
          <TableauExportActions exportTableauView={exportTableauView} isDashboardView={isDashboardView} />
        </Paper>
        <div className={classes.pageBody}>
          <Switch>
            <Redirect exact from={`${match.path}/`} to={urljoin(match.path, allowedTabs?.[0]?.url)} />
            {allowedTabs.map((tab) => (
              <Route
                key={tab.url}
                path={urljoin(match.path, tab.url)}
                render={(routerProps) => {
                  return (
                    <div style={{ padding: '24px 0px' }}>
                      <tab.component
                        classes={classes}
                        {...routerProps}
                        {...tab.props}
                        onLoad={setExportTableauView}
                        onFilterApplied={onFilterApplied}
                      />
                    </div>
                  );
                }}
              />
            ))}
            <Redirect to={match.path} />
          </Switch>
        </div>
      </CardDialog>
    </>
  );
};

HeaderTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      component: PropTypes.func.isRequired,
      props: PropTypes.shape({
        action: PropTypes.string,
        verb: PropTypes.string,
      }),
    })
  ),
};

HeaderTabs.defaultProps = {
  tabs: [],
};
